/*! Copyright (c) Safe As Milk. All rights reserved. */
import Animations from "animations";

class DynamicProductSearch extends HTMLElement {
  #boundLinksHandler;
  #boundLinksHandlerWithScroll;
  #boundPopStateHandler;
  constructor() {
    super();
    this.#boundLinksHandler = this.#linksHandler.bind(this);
    this.#boundLinksHandlerWithScroll = this.#linksHandlerWithScroll.bind(this);
    this.#boundPopStateHandler = this.#popStateListener.bind(this);
  }
  connectedCallback() {
    this.abortController = null;
    this.listeners = [];
    this.section = this.closest(".section");
    this.updateLoading();
    
    // Ensure initialization happens after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
    this.#setUpListeners();
      });
    } else {
      // Use setTimeout to ensure forms are available (even if hidden)
      setTimeout(() => {
    this.#setUpListeners();
      }, 0);
    }
    
    window.addEventListener("popstate", this.#boundPopStateHandler);
  }
  disconnectedCallback() {
    this.#removeListeners();
    window.removeEventListener("popstate", this.#boundPopStateHandler);
  }
  renderError(e = window.theme.localize("ERROR_PRODUCTS")) {
    if (
      this.previousElementSibling &&
      this.previousElementSibling.classList.contains("error")
    ) {
      this.previousElementSibling.remove();
    }
    const error = document.createElement("div");
    error.classList.add("error");
    error.innerHTML = e;
    this.section.prepend(error);
  }
  renderCollection(params) {
    const {
      query: query,
      updateURL: updateURL,
      scrollUp: scrollUp,
    } = {
      query: "",
      updateURL: true,
      scrollUp: false,
      ...params,
    };
    const currentFocusElementId =
      document.activeElement && document.activeElement.getAttribute("id");
    const sectionClass = `.section--${this.dataset.section}`;
    this.abortController = new AbortController();
    this.updateLoading(true);
    fetch(`${this.dataset.url}?${query}&section_id=${this.dataset.section}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (response.ok) return response.text();
        throw new Error(window.theme.localize("ERROR_PRODUCTS"));
      })
      .then((responseText) => {
        this.dispatchEvent(
          new CustomEvent("on:dynamic-product-search:loading")
        );
        let sidebarContentOffset = 0;
        const predictiveSearch = this.querySelector("predictive-search");
        if (predictiveSearch) predictiveSearch.close();
        const openFiltersModal = this.querySelector(
          '.modal--filters:not([aria-hidden="true"])'
        );
        if (openFiltersModal) {
          const content = openFiltersModal.querySelector(
            ".collection-sidebar__wrapper"
          );
          if (content) {
            sidebarContentOffset = content.scrollTop;
          }
        }
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const destination = document.querySelector(sectionClass);
        const source = html.querySelector(sectionClass);
        if (source && destination) {
          this.#removeListeners();
          const destinationMainContainer =
            destination.querySelector(".js-main-content");
          const sourceMainContainer = source.querySelector(".js-main-content");
          if (
            destinationMainContainer.children.length > 0 &&
            sourceMainContainer.children.length > 0
          ) {
            const destinationResultsTotal =
              destination.querySelector(`.js-results-total`);
            const destinationSortForm =
              destination.querySelector(`.js-sort-form`);
            const destinationActiveFilters =
              destination.querySelector(`.js-active-filters`);
            const destinationFilters = destination.querySelector(`.js-filters`);
            const destinationActions = destination.querySelector(`.js-actions`);
            const destinationResultsContainer = destination.querySelector(
              `.js-results-container`
            );
            const destinationResults = destination.querySelector(`.js-results`);
            const destinationPagination =
              destination.querySelector(`.js-pagination`);
            const sourceResultsTotal =
              source.querySelector(`.js-results-total`);
            const sourceSortForm = source.querySelector(`.js-sort-form`);
            const sourceActiveFilters =
              source.querySelector(`.js-active-filters`);
            const sourceFilters = source.querySelector(`.js-filters`);
            const sourceActions = source.querySelector(`.js-actions`);
            const sourceResultsContainer = source.querySelector(
              `.js-results-container`
            );
            const sourceResults = source.querySelector(`.js-results`);
            const sourcePagination = source.querySelector(`.js-pagination`);
            if (destinationResultsTotal && sourceResultsTotal) {
              destinationResultsTotal.innerHTML = sourceResultsTotal.innerHTML;
            }
            if (destinationResultsContainer && sourceResultsContainer) {
              if (destinationSortForm && sourceSortForm)
                destinationSortForm.innerHTML = sourceSortForm.innerHTML;
              if (destinationActiveFilters && sourceActiveFilters)
                destinationActiveFilters.innerHTML =
                  sourceActiveFilters.innerHTML;
              if (destinationFilters && sourceFilters)
                destinationFilters.innerHTML = sourceFilters.innerHTML;
              if (destinationActions && sourceActions)
                destinationActions.innerHTML = sourceActions.innerHTML;
              if (destinationResults && sourceResults)
                destinationResults.innerHTML = sourceResults.innerHTML;
            } else if (destinationResultsContainer) {
              destinationResultsContainer.remove();
            } else {
              destinationMainContainer.appendChild(sourceResultsContainer);
            }
            if (destinationPagination && sourcePagination) {
              destinationPagination.innerHTML = sourcePagination.innerHTML;
            } else if (destinationPagination) {
              destinationPagination.remove();
            } else if (sourcePagination) {
              destination
                .querySelector(".js-main-content")
                .appendChild(sourcePagination);
            }

            // --- NEW: Update both grid and list containers ---
            // Update grid view (cards)
            const destinationGrid =
              destination.querySelector(".collection__cards");
            const sourceGrid = source.querySelector(".collection__cards");
            if (destinationGrid && sourceGrid) {
              destinationGrid.innerHTML = sourceGrid.innerHTML;
            }
            // Update list view (table)
            const destinationTable = destination.querySelector(
              ".collection__table-view"
            );
            const sourceTable = source.querySelector(".collection__table-view");
            if (destinationTable && sourceTable) {
              const destinationTableBody =
                destinationTable.querySelector("tbody");
              const sourceTableBody = sourceTable.querySelector("tbody");
              if (destinationTableBody && sourceTableBody) {
                destinationTableBody.innerHTML = sourceTableBody.innerHTML;
              }
            }
            // --- END NEW ---
          } else {
            const destinationSectionTitle =
              destination.querySelector(".js-section-title");
            const sourceSectionTitle =
              source.querySelector(".js-section-title");
            if (destinationSectionTitle && sourceSectionTitle)
              destinationSectionTitle.innerHTML = sourceSectionTitle.innerHTML;
            if (destinationMainContainer && sourceMainContainer)
              destinationMainContainer.innerHTML =
                sourceMainContainer.innerHTML;
          }
          this.#setUpListeners();

          // Re-initialize table functionality after content update
          this.#initializeTableFunctionality();
        }
        Animations.setup(document.querySelector(sectionClass));
        if (openFiltersModal) {
          const content = openFiltersModal.querySelector(
            ".collection-sidebar__wrapper"
          );
          if (content && sidebarContentOffset !== 0) {
            content.scrollTop = sidebarContentOffset;
          }
        }
        if (scrollUp) this.scrollUp();
        const newPageTitle = document.querySelector(
          `${sectionClass} dynamic-product-search`
        ).dataset.pageTitle;
        if (updateURL) this.updateURL(query, newPageTitle);
        document.title = newPageTitle;
        this.updateLoading(false);
        if (
          this.dataset.quickShopDynamicCheckout === "true" &&
          window.Shopify &&
          window.Shopify.PaymentButton
        )
          window.Shopify.PaymentButton.init();
        const currentlyFocusedElement = document.getElementById(
          currentFocusElementId
        );
        if (currentlyFocusedElement) {
          currentlyFocusedElement.focus();
        }
        this.dispatchEvent(new CustomEvent("on:dynamic-product-search:loaded"));
      })
      .catch(async (error) => {
        if (error.name && error.name === "AbortError") return;
        this.updateLoading(false);
        this.renderError(error);
        const openFiltersModal = this.querySelector(
          '.modal--filters:not([aria-hidden="true"])'
        );
        if (openFiltersModal) await openFiltersModal.close();
        this.scrollUp();
        this.dispatchEvent(new CustomEvent("on:dynamic-product-search:failed"));
      });
  }
  scrollUp() {
    const { top: top } = this.section.getBoundingClientRect();
    window.scrollBy({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
      top: top,
    });
  }
  updateLoading(isLoading = false) {
    this.loading = isLoading;
    if (isLoading) {
      this.section.classList.add("is-loading");
    } else {
      this.section.classList.remove("is-loading");
    }
  }
  updateURL(query, title = "") {
    if (this.dataset.updateUrl === "false") return;
    window.history.pushState(
      {},
      title,
      `${this.dataset.url}${query ? `?${query}` : ""}`
    );
  }
  #popStateListener() {
    const query = window.location.search.slice(1);
    this.renderCollection({
      updateURL: false,
      query: query,
    });
  }
  #linksHandler(e, scrollUp = false) {
    e.preventDefault();
    if (!e.target.getAttribute("href") && !e.target.dataset.url) return;
    const url = e.target.getAttribute("href") || e.target.dataset.url;
    const query = url.split("?").length > 0 ? url.split("?")[1] : "";
    this.renderCollection({
      query: query,
      scrollUp: scrollUp,
    });
  }
  #linksHandlerWithScroll(e) {
    this.#linksHandler(e, true);
  }
  #filtersControl() {
    const filters = this.querySelectorAll(".js-filter-trigger");
    filters.forEach((filter) => {
      filter.addEventListener("click", this.#boundLinksHandler);
      this.listeners.push({
        element: filter,
        event: "click",
        handler: this.#boundLinksHandler,
      });
    });
  }
  #paginationControl() {
    const paginationLinks = this.querySelectorAll(".pagination a");
    paginationLinks.forEach((paginationLink) => {
      paginationLink.addEventListener(
        "click",
        this.#boundLinksHandlerWithScroll
      );
      this.listeners.push({
        element: paginationLink,
        event: "click",
        handler: this.#boundLinksHandlerWithScroll,
      });
    });
  }
  #setUpListeners() {
    this.forms = [
      this.querySelector(".js-search-form"),
      this.querySelector(".js-sort-form"),
      this.querySelector(".js-filters-form"),
    ].filter((f) => f);
    if (
      this.querySelector(".js-search-form") &&
      this.querySelector(".js-filters-form")
    ) {
      this.querySelector('.js-filters-form input[name="q"]').remove();
    }
    this.forms.forEach((form) => {
      if (form.dataset.submitOnChange === "true") {
        form.addEventListener("change", () => {
          form.requestSubmit();
        });
      }
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        if (e.stopPropagation) e.stopPropagation();
        if (this.abortController) this.abortController.abort();
        let query = "";
        if (e.target.classList.contains("js-search-form")) {
          const paramsToKeep = ["type", "q", "sort_by"];
          query = this.forms.reduce((qs, f) => {
            const thisFormData = new FormData(f);
            for (const [paramName] of thisFormData.entries()) {
              if (!paramsToKeep.includes(paramName))
                thisFormData.delete(paramName);
            }
            const thisQueryString = new URLSearchParams(
              thisFormData
            ).toString();
            return `${qs}${
              thisQueryString ? `${qs ? "&" : ""}${thisQueryString}` : ""
            }`;
          }, "");
        } else {
          query = this.forms.reduce((qs, f) => {
            const thisFormData = new FormData(f);
            const thisQueryString = new URLSearchParams(
              thisFormData
            ).toString();
            return `${qs}${
              thisQueryString ? `${qs ? "&" : ""}${thisQueryString}` : ""
            }`;
          }, "");
        }
        this.renderCollection({
          query: query,
        });
      });
    });
    this.#filtersControl();
    this.#paginationControl();
    this.#initializeTableFunctionality();
    this.#viewToggleControl();
  }
  #removeListeners() {
    this.listeners.forEach(
      ({ element: element, event: event, handler: handler }) => {
        element.removeEventListener(event, handler);
      }
    );
  }

  #initializeTableFunctionality() {
    // Re-initialize table add to cart forms
    const tableForms = this.querySelectorAll(".js-table-add-to-cart-form");
    tableForms.forEach((form) => {
      // Check if listener already exists to avoid duplicates
      if (form.dataset.listenerAttached === "true") {
        return;
      }
      form.dataset.listenerAttached = "true";

      form.addEventListener("submit", (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        
        // Prevent duplicate submissions
        if (form.dataset.submitting === "true") {
          return;
        }
        form.dataset.submitting = "true";
        
        // Build form data manually to ensure correct quantity
        const formData = new FormData();
        
        // Get variant ID
        const variantSelector = form
          .closest("tr")
          .querySelector(".variant-selector");
        let variantId = null;
        if (variantSelector) {
          variantId = variantSelector.value;
        } else {
          const hiddenId = form.querySelector('input[name="id"]');
          if (hiddenId) {
            variantId = hiddenId.value;
          }
        }
        
        if (!variantId) {
          alert("Fehler: Produktvariante nicht gefunden");
          form.dataset.submitting = "false";
          return;
        }
        
        // Get quantity from quantity-input custom element (important!)
        const quantityInputElement = form.querySelector("quantity-input");
        let quantity = 1;
        if (quantityInputElement && quantityInputElement.value) {
          quantity = parseInt(quantityInputElement.value);
          if (isNaN(quantity) || quantity < 1) {
            quantity = 1;
          }
        } else {
          // Fallback to inner input if custom element not available
          const quantityInput = form.querySelector('input[name="quantity"]');
          if (quantityInput) {
            quantity = parseInt(quantityInput.value);
            if (isNaN(quantity) || quantity < 1) {
              quantity = 1;
            }
          }
        }
        
        // Set form data
        formData.set("id", variantId);
        formData.set("quantity", quantity.toString());
        
        const button = form.querySelector('button[type="submit"]');
        if (button) {
          // Temporarily neutralize external click handlers
          button.dataset.originalClasses = button.className;
          button.classList.add("loading");
          button.classList.add("is-submitting");
          button.disabled = true;
        }
        
        // Use modern Cart.add() method if available
        if (window.Cart && typeof window.Cart.add === "function") {
          const item = {
            id: parseInt(variantId),
            quantity: quantity
          };
          
          window.Cart.add(item)
            .then(async (cart) => {
              // Cart.add() automatically:
              // 1. Updates cart state (cart count will auto-update via Cart.subscribe)
              // 2. Sets latestAddedProduct (notification will auto-update via CartNotification subscription)
              
              // CRITICAL: Update cart count IMMEDIATELY (before async operations)
              // IMPORTANT: Read count BEFORE Cart.add() changes it, or use cart response
              let cartItemCount = null;
              
              // Method 1: Get from cart.add() response FIRST (most accurate - already includes new item)
              if (cart && cart.item_count !== undefined) {
                cartItemCount = cart.item_count;
                console.log("[GRID VIEW DEBUG] Got cart count from Cart.add() response:", cartItemCount);
              }
              
              // Method 2: Try to get from Cart state (if response doesn't have it)
              if (cartItemCount === null && window.Cart && typeof window.Cart.getState === "function") {
                try {
                  const cartState = window.Cart.getState();
                  if (cartState?.cart?.item_count !== undefined) {
                    cartItemCount = cartState.cart.item_count;
                    console.log("[GRID VIEW DEBUG] Got cart count from Cart state:", cartItemCount);
                  }
                } catch (e) {
                  console.warn("[GRID VIEW DEBUG] Could not get cart state:", e);
                }
              }
              
              // Method 3: Read current count from DOM and add quantity (fallback if Cart methods fail)
              if (cartItemCount === null) {
                try {
                  const currentItemsCountElement = document.querySelector("items-count");
                  if (currentItemsCountElement && currentItemsCountElement.innerHTML) {
                    const currentCount = parseInt(currentItemsCountElement.innerHTML.trim()) || 0;
                    cartItemCount = currentCount + quantity;
                    console.log("[GRID VIEW DEBUG] Calculated cart count from DOM (current + quantity):", currentCount, "+", quantity, "=", cartItemCount);
                  }
                } catch (e) {
                  console.warn("[GRID VIEW DEBUG] Could not read current count from DOM:", e);
                }
              }
              
              // Method 4: Last resort - if still null, just use quantity (assume empty cart)
              if (cartItemCount === null) {
                cartItemCount = quantity;
                console.log("[GRID VIEW DEBUG] Using quantity as cart count (last resort):", cartItemCount);
              }
              
              // ALWAYS update cart count IMMEDIATELY (even if window.Cart is not fully available)
              console.log("[GRID VIEW DEBUG] ===== CART COUNT UPDATE START =====");
              console.log("[GRID VIEW DEBUG] cartItemCount calculated:", cartItemCount);
              
              if (cartItemCount !== null && cartItemCount !== undefined) {
                console.log("[GRID VIEW DEBUG] IMMEDIATELY updating cart count to:", cartItemCount);
                
                // Direct DOM update - find ALL items-count elements directly (MOST RELIABLE)
                const allItemsCount = document.querySelectorAll("items-count");
                console.log("[GRID VIEW DEBUG] Found", allItemsCount.length, "items-count element(s) in DOM");
                
                let directUpdated = 0;
                allItemsCount.forEach((itemsCount, index) => {
                  const oldValue = itemsCount.innerHTML;
                  itemsCount.innerHTML = cartItemCount.toString();
                  directUpdated++;
                  console.log("[GRID VIEW DEBUG] Updated items-count #" + index + " from '" + oldValue + "' to '" + cartItemCount + "'");
                });
                console.log("[GRID VIEW DEBUG] ✓ Directly updated", directUpdated, "items-count element(s)");
                
                // Also update via cart-link components (for subscription sync)
                const cartLinks = document.querySelectorAll("cart-link");
                let cartLinkUpdated = 0;
                cartLinks.forEach((cartLink, index) => {
                  const itemsCount = cartLink.querySelector("items-count");
                  if (itemsCount) {
                    itemsCount.innerHTML = cartItemCount.toString();
                    cartLinkUpdated++;
                  }
                });
                console.log("[GRID VIEW DEBUG] ✓ Updated", cartLinkUpdated, "cart-link(s)");
                
                // Also update cart-quantity-info components
                const cartQuantityInfos = document.querySelectorAll("cart-quantity-info");
                let quantityInfoUpdated = 0;
                cartQuantityInfos.forEach((cartQuantityInfo, index) => {
                  const itemsCount = cartQuantityInfo.querySelector("items-count");
                  if (itemsCount) {
                    itemsCount.innerHTML = cartItemCount.toString();
                    quantityInfoUpdated++;
                  }
                });
                console.log("[GRID VIEW DEBUG] ✓ Updated", quantityInfoUpdated, "cart-quantity-info element(s)");
                
                // Final verification
                const verifyCount = document.querySelector("items-count");
                if (verifyCount) {
                  console.log("[GRID VIEW DEBUG] ✓✓✓ VERIFICATION - First items-count now shows:", verifyCount.innerHTML, "✓✓✓");
                }
                console.log("[GRID VIEW DEBUG] ===== CART COUNT UPDATE END =====");
              }
              
              // CRITICAL: Force cart state refresh to ensure subscribers (cart-link, cart-quantity-info) update
              // Cart.add() updates internally but we need to explicitly refresh to trigger subscriptions
              if (window.Cart && typeof window.Cart.getCart === "function") {
                try {
                  await window.Cart.getCart();
                  console.log("[GRID VIEW DEBUG] Cart state refreshed after add");
                } catch (e) {
                  console.warn("[GRID VIEW DEBUG] Could not refresh cart:", e);
                }
              }
              
              // Minimal wait for subscribers (count already updated above)
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // Force manual update again after getCart (subscriptions might override)
              const cartState = window.Cart?.getState?.();
              if (cartState?.cart?.item_count !== undefined) {
                const updatedCount = cartState.cart.item_count;
                // Update only if different (to avoid unnecessary DOM manipulation)
                if (updatedCount !== cartItemCount) {
                  console.log("[GRID VIEW DEBUG] Updating cart count again after getCart to:", updatedCount);
                  const allItemsCount = document.querySelectorAll("items-count");
                  allItemsCount.forEach((itemsCount) => {
                    itemsCount.innerHTML = updatedCount.toString();
                  });
                }
              }
              
              if (button) {
                button.classList.remove("loading");
                button.classList.add("added");
                setTimeout(() => {
                  button.classList.remove("added");
                }, 1500);
              }
              
              // Check if product-form has notify-on-add attribute
              const productFormElement = form.closest("product-form");
              const shouldNotify = productFormElement && productFormElement.hasAttribute("notify-on-add");
              
              if (shouldNotify) {
                // Reset variants being added (same as product-form.js)
                if (window.Cart && typeof window.Cart.resetVariantsBeingAdded === "function") {
                  await window.Cart.resetVariantsBeingAdded();
                }
                
                // Wait for CartNotification component to render with latestAddedProduct
                // Verify latestAddedProduct is available in state
                let retries = 0;
                let cartState = null;
                while (retries < 20) {
                  cartState = window.Cart?.getState?.();
                  if (cartState?.latestAddedProduct) {
                    break;
                  }
                  await new Promise(resolve => setTimeout(resolve, 50));
                  retries++;
                }
                
                // Ensure we have the latestAddedProduct
                if (!cartState?.latestAddedProduct) {
                  console.warn("latestAddedProduct not found after retries");
                }
                
                // Find cart-notification element and wait for content to render
                const cartNotificationElement = document.querySelector("cart-notification");
                
                // Wait for CartNotification subscription to fire and DOM to update
                // The subscription should automatically call #render() when latestAddedProduct changes
                if (cartNotificationElement && cartState?.latestAddedProduct) {
                  // Ensure the element is connected (subscription might not be active if element is hidden)
                  if (!cartNotificationElement.isConnected) {
                    // Element not in DOM yet, wait a bit
                    await new Promise(resolve => setTimeout(resolve, 100));
                  }
                  
                  // Set item property to ensure it's available (subscription should have already fired)
                  cartNotificationElement.item = cartState.latestAddedProduct;
                  
                  // Wait for the title to be populated (indicates render completed)
                  // CartNotification subscription should fire automatically when latestAddedProduct changes
                  let contentRetries = 0;
                  let titleRendered = false;
                  while (contentRetries < 40) {
                    const titleElement = cartNotificationElement.querySelector("cart-item-title");
                    if (titleElement && titleElement.innerHTML && titleElement.innerHTML.trim() !== "") {
                      titleRendered = true;
                      // Title is rendered, that's the main indicator
                      // Image might still be loading but title is sufficient
                      break;
                    }
                    await new Promise(resolve => setTimeout(resolve, 50));
                    contentRetries++;
                    
                    // Halfway through, ensure item is set (might help if subscription didn't fire)
                    if (contentRetries === 20 && cartState?.latestAddedProduct) {
                      cartNotificationElement.item = cartState.latestAddedProduct;
                      // Give it a moment to process
                      await new Promise(resolve => setTimeout(resolve, 50));
                    }
                  }
                  
                  // ALWAYS manually update DOM to ensure correct content (especially if popup is already open)
                  if (cartState?.latestAddedProduct && cartNotificationElement) {
                    const itemToRender = cartState.latestAddedProduct;
                    console.log("[GRID VIEW DEBUG] ALWAYS updating DOM with item:", itemToRender.product_title || itemToRender.title);
                    
                    // Update cartNotificationElement.item first
                    cartNotificationElement.item = itemToRender;
                    
                    // Update title
                    const titleElement = cartNotificationElement.querySelector("cart-item-title");
                    const itemTitle = itemToRender.product_title || itemToRender.title;
                    if (titleElement && itemTitle) {
                      titleElement.innerHTML = itemTitle;
                      console.log("[GRID VIEW DEBUG] Updated title:", itemTitle);
                    }
                    
                    // Update image if available
                    const imageElement = cartNotificationElement.querySelector("cart-item-image-container img");
                    if (imageElement && itemToRender.featured_image?.url) {
                      const imageData = itemToRender.featured_image;
                      const { url, width, height } = imageData;
                      
                      if (url) {
                        // Calculate srcset (similar to CartNotification component)
                        const srcsetWidths = [120, 240, 360, 480];
                        let srcset = "";
                        const aspectRatio = width && height ? width / height : 1;
                        
                        srcsetWidths.forEach(srcWidth => {
                          if (width && srcWidth <= width) {
                            const srcHeight = Math.round(srcWidth / aspectRatio);
                            srcset += `${url}${url.includes("?") ? "&" : "?"}width=${srcWidth}&height=${srcHeight}&crop=center ${srcWidth}w ${srcHeight}h, `;
                          }
                        });
                        
                        // Set image attributes
                        imageElement.setAttribute("src", url);
                        if (srcset) {
                          imageElement.setAttribute("srcset", srcset.trim().replace(/,\s*$/, ""));
                        }
                        if (width) imageElement.setAttribute("width", width);
                        if (height) imageElement.setAttribute("height", height);
                        imageElement.setAttribute("alt", `Image for ${itemTitle}`);
                        console.log("[GRID VIEW DEBUG] Updated image:", url);
                      }
                    }
                    
                    // Update variant title if available
                    const variantProperty = cartNotificationElement.querySelector("cart-item-default-property");
                    if (variantProperty) {
                      if (itemToRender.variant_title && !itemToRender.variant_title.includes("Default")) {
                        const metaTemplate = document.getElementById("template-cart-item-meta");
                        if (metaTemplate) {
                          variantProperty.innerHTML = "";
                          variantProperty.appendChild(metaTemplate.content.cloneNode(true));
                          const propValue = variantProperty.querySelector("property-value");
                          if (propValue) {
                            propValue.innerHTML = itemToRender.variant_title;
                          }
                        }
                      } else {
                        variantProperty.innerHTML = "";
                      }
                    }
                    
                    // Update vendor if available
                    const vendorElement = cartNotificationElement.querySelector("cart-item-vendor");
                    if (vendorElement && itemToRender.vendor) {
                      const metaTemplate = document.getElementById("template-cart-item-meta");
                      if (metaTemplate) {
                        vendorElement.innerHTML = "";
                        vendorElement.appendChild(metaTemplate.content.cloneNode(true));
                        const propValue = vendorElement.querySelector("property-value");
                        if (propValue) {
                          propValue.innerHTML = itemToRender.vendor;
                        }
                      }
                    }
                    
                    // Wait a bit for DOM updates to settle
                    await new Promise(resolve => setTimeout(resolve, 100));
                  }
                  
                  // Additional wait for image to load if title is rendered
                  if (titleRendered) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                  }
                  
                  // Final verification that content exists
                  const titleElement = cartNotificationElement.querySelector("cart-item-title");
                  if (!titleElement || !titleElement.innerHTML || titleElement.innerHTML.trim() === "") {
                    console.warn("CartNotification: Title element missing or empty");
                  }
                } else {
                  // Fallback: wait a bit for CartNotification to initialize
                  await new Promise(resolve => setTimeout(resolve, 300));
                }
                
                // Open cart notification/drawer (check if not already in cart modal)
                if (!form.closest(".modal--cart")) {
                  // Try popup-dialog first (for popup notification)
                  let cartNotification = document.querySelector(
                    "popup-dialog.popup--cart-notification, popup-dialog#popup-cart-notification"
                  );
                  
                  // Fallback to modal-dialog or elements with classes
                  if (!cartNotification) {
                    cartNotification = document.querySelector(
                      "modal-dialog.modal--cart, .modal--cart, .popup--cart-notification"
                    );
                  }
                  
                  if (cartNotification) {
                    // DISABLE auto-close - remove dismissAfterInterval attribute
                    if (cartNotification.hasAttribute("data-dismiss-after-interval")) {
                      cartNotification.removeAttribute("data-dismiss-after-interval");
                    }
                    
                    // Stop interval if it exists BEFORE opening
                    if (cartNotification.interval) {
                      try {
                        if (typeof cartNotification.interval.stop === "function") {
                          cartNotification.interval.stop();
                        }
                      } catch (e) {}
                      try {
                        if (typeof cartNotification.interval.reset === "function") {
                          cartNotification.interval.reset();
                        }
                      } catch (e) {}
                      cartNotification.interval = null;
                    }
                    
                    // Listen for popup opened event to stop interval if it restarts
                    const stopIntervalOnOpen = () => {
                      if (cartNotification.interval) {
                        try {
                          if (typeof cartNotification.interval.stop === "function") {
                            cartNotification.interval.stop();
                          }
                        } catch (e) {}
                        cartNotification.interval = null;
                      }
                      cartNotification.removeEventListener("on:popup:opened", stopIntervalOnOpen);
                    };
                    cartNotification.addEventListener("on:popup:opened", stopIntervalOnOpen);
                    
                    // Check if it's a popup-dialog or modal-dialog element
                    if (cartNotification.tagName === "POPUP-DIALOG" || cartNotification.tagName === "MODAL-DIALOG") {
                      if (!cartNotification.shown && typeof cartNotification.open === "function") {
                        await cartNotification.open();
                        // Immediately stop interval after opening
                        setTimeout(stopIntervalOnOpen, 50);
                      } else if (typeof cartNotification.open === "function") {
                        await cartNotification.open();
                        setTimeout(stopIntervalOnOpen, 50);
                      }
                    } else {
                      // Legacy support for elements with classes
                      if (!cartNotification.shown && typeof cartNotification.open === "function") {
                        await cartNotification.open();
                        setTimeout(stopIntervalOnOpen, 50);
                      }
                    }
                  } else if (window.theme && window.theme.mfpOpen) {
                    // Fallback to theme's method
                    window.theme.mfpOpen("cart");
                  }
                }
              } else {
                // Fallback: reset latestAddedProduct and variants if notify-on-add not present
                if (window.Cart && typeof window.Cart.resetLatestAddedProduct === "function") {
                  await window.Cart.resetLatestAddedProduct();
                }
                if (window.Cart && typeof window.Cart.resetVariantsBeingAdded === "function") {
                  await window.Cart.resetVariantsBeingAdded(item.id);
                }
              }
            })
            .catch((error) => {
              if (button) button.classList.remove("loading");
              alert("Fehler beim Hinzufügen zum Warenkorb: " + (error.message || "Unknown error"));
            })
            .finally(() => {
              form.dataset.submitting = "false";
              if (button) {
                button.disabled = false;
                button.classList.remove("is-submitting");
                if (button.dataset.originalClasses) {
                  button.className = button.dataset.originalClasses;
                  delete button.dataset.originalClasses;
                }
              }
            });
        } else {
          // Fallback to fetch - build item object for Cart.add() if needed
          const fallbackItem = {
            id: parseInt(variantId),
            quantity: quantity
          };
          
        fetch("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        })
          .then((response) => {
            if (!response.ok) throw new Error("Network error");
            return response.json();
          })
            .then(async (data) => {
              console.log("[LIST VIEW DEBUG] Fetch response:", data);
              console.log("[LIST VIEW DEBUG] window.Cart exists:", !!window.Cart);
              console.log("[LIST VIEW DEBUG] window.Cart methods:", {
                getCart: typeof window.Cart?.getCart,
                getState: typeof window.Cart?.getState,
                setState: typeof window.Cart?.setState,
                add: typeof window.Cart?.add
              });
              
              // Fetch already added the item, now we need to:
              // 1. Update cart count IMMEDIATELY from response
              // 2. Update cart state (for subscribers)
              // 3. Set latestAddedProduct (for notification popup)
              
              // CRITICAL: Update cart count IMMEDIATELY 
              // Calculate new count from current cart + quantity added
              let cartItemCount = null;
              
              // Method 1: Read current count from DOM (most reliable - works even without Cart object)
              try {
                const currentItemsCountElement = document.querySelector("items-count");
                if (currentItemsCountElement && currentItemsCountElement.innerHTML) {
                  const currentCount = parseInt(currentItemsCountElement.innerHTML) || 0;
                  cartItemCount = currentCount + quantity;
                  console.log("[LIST VIEW DEBUG] Calculated cart count from DOM (current + quantity):", currentCount, "+", quantity, "=", cartItemCount);
                }
              } catch (e) {
                console.warn("[LIST VIEW DEBUG] Could not read current count from DOM:", e);
              }
              
              // Method 2: Try to get from Cart state and add quantity (if available)
              if (cartItemCount === null && window.Cart && typeof window.Cart.getState === "function") {
                try {
                  const currentCartState = window.Cart.getState();
                  if (currentCartState?.cart?.item_count !== undefined) {
                    cartItemCount = currentCartState.cart.item_count + quantity;
                    console.log("[LIST VIEW DEBUG] Calculated cart count from Cart state (current + quantity):", currentCartState.cart.item_count, "+", quantity, "=", cartItemCount);
                  }
                } catch (e) {
                  console.warn("[LIST VIEW DEBUG] Could not get current cart state:", e);
                }
              }
              
              // Method 3: Fallback to fetch response item_count or items length (if available)
              if (cartItemCount === null && data) {
                if (data.item_count !== undefined) {
                  cartItemCount = data.item_count;
                  console.log("[LIST VIEW DEBUG] Got cart count from fetch response.item_count:", cartItemCount);
                } else if (data.items && Array.isArray(data.items)) {
                  // Calculate total quantity from items
                  cartItemCount = data.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
                  console.log("[LIST VIEW DEBUG] Calculated cart count from fetch response.items:", cartItemCount);
                }
              }
              
              // Method 4: If still null, assume we're adding to empty cart (count = quantity)
              if (cartItemCount === null) {
                cartItemCount = quantity;
                console.log("[LIST VIEW DEBUG] Using quantity as cart count (assumed empty cart):", cartItemCount);
              }
              
              // ALWAYS update cart count (even if window.Cart is not available)
              // This ensures count updates immediately without refresh
              console.log("[LIST VIEW DEBUG] ===== CART COUNT UPDATE START =====");
              console.log("[LIST VIEW DEBUG] cartItemCount calculated:", cartItemCount);
              
              if (cartItemCount !== null && cartItemCount !== undefined) {
                console.log("[LIST VIEW DEBUG] Updating cart count to:", cartItemCount);
                
                // Method 1: Direct DOM update - find ALL items-count elements directly (MOST RELIABLE)
                const allItemsCount = document.querySelectorAll("items-count");
                console.log("[LIST VIEW DEBUG] Found", allItemsCount.length, "items-count element(s) in DOM");
                
                let directUpdated = 0;
                allItemsCount.forEach((itemsCount, index) => {
                  const oldValue = itemsCount.innerHTML;
                  itemsCount.innerHTML = cartItemCount.toString();
                  directUpdated++;
                  console.log("[LIST VIEW DEBUG] Updated items-count #" + index + " from '" + oldValue + "' to '" + cartItemCount + "'");
                });
                console.log("[LIST VIEW DEBUG] ✓ Directly updated", directUpdated, "items-count element(s)");
                
                // Method 2: Update via cart-link custom elements (for subscription sync)
                const cartLinks = document.querySelectorAll("cart-link");
                let cartLinkUpdated = 0;
                cartLinks.forEach((cartLink, index) => {
                  const itemsCount = cartLink.querySelector("items-count");
                  if (itemsCount) {
                    itemsCount.innerHTML = cartItemCount.toString();
                    cartLinkUpdated++;
                    console.log("[LIST VIEW DEBUG] Updated cart-link #" + index + " count to:", cartItemCount);
                  }
                });
                console.log("[LIST VIEW DEBUG] ✓ Updated", cartLinkUpdated, "cart-link(s)");
                
                // Method 3: Update cart-quantity-info components
                const cartQuantityInfos = document.querySelectorAll("cart-quantity-info");
                let quantityInfoUpdated = 0;
                cartQuantityInfos.forEach((cartQuantityInfo, index) => {
                  const itemsCount = cartQuantityInfo.querySelector("items-count");
                  if (itemsCount) {
                    itemsCount.innerHTML = cartItemCount.toString();
                    quantityInfoUpdated++;
                    console.log("[LIST VIEW DEBUG] Updated cart-quantity-info #" + index + " count to:", cartItemCount);
                  }
                });
                console.log("[LIST VIEW DEBUG] ✓ Updated", quantityInfoUpdated, "cart-quantity-info element(s)");
                
                // Final verification
                const verifyCount = document.querySelector("items-count");
                if (verifyCount) {
                  console.log("[LIST VIEW DEBUG] ✓✓✓ VERIFICATION - First items-count now shows:", verifyCount.innerHTML, "✓✓✓");
                } else {
                  console.warn("[LIST VIEW DEBUG] ⚠ WARNING - No items-count element found for verification!");
                }
                console.log("[LIST VIEW DEBUG] ===== CART COUNT UPDATE END =====");
              } else {
                console.error("[LIST VIEW DEBUG] ❌ ERROR - Could not calculate cart count! cartItemCount is:", cartItemCount);
              }
              
              // IMPORTANT: Reset latestAddedProduct first to prevent showing old content
              if (window.Cart && typeof window.Cart.resetLatestAddedProduct === "function") {
                try {
                  await window.Cart.resetLatestAddedProduct();
                  console.log("[LIST VIEW DEBUG] Reset latestAddedProduct");
                } catch (e) {
                  console.warn("[LIST VIEW DEBUG] Could not reset latestAddedProduct:", e);
                }
              }
              
              // CRITICAL: Update cart state (this triggers cart count update via subscriptions)
              // But we already updated manually above, so minimal wait here (like grid view)
              if (window.Cart && typeof window.Cart.getCart === "function") {
                try {
                  await window.Cart.getCart();
                  console.log("[LIST VIEW DEBUG] Cart state updated via getCart");
                  // Minimal wait for subscribers (like grid view)
                  await new Promise(resolve => setTimeout(resolve, 100));
                  
                  // Force manual update after getCart (subscriptions might not fire)
                  const cartState = window.Cart?.getState?.();
                  if (cartState?.cart?.item_count !== undefined) {
                    const updatedCount = cartState.cart.item_count;
                    console.log("[LIST VIEW DEBUG] Updating cart count after getCart to:", updatedCount);
                    
                    // Update ALL items-count elements directly (most reliable)
                    const allItemsCount = document.querySelectorAll("items-count");
                    allItemsCount.forEach((itemsCount) => {
                      itemsCount.innerHTML = updatedCount.toString();
                    });
                    console.log("[LIST VIEW DEBUG] Updated", allItemsCount.length, "items-count element(s) after getCart");
                    
                    // Also update via cart-link components (for subscription sync)
                    const cartLinks = document.querySelectorAll("cart-link");
                    cartLinks.forEach((cartLink) => {
                      const itemsCount = cartLink.querySelector("items-count");
                      if (itemsCount) {
                        itemsCount.innerHTML = updatedCount.toString();
                      }
                    });
                    
                    // Also update cart-quantity-info components
                    const cartQuantityInfos = document.querySelectorAll("cart-quantity-info");
                    cartQuantityInfos.forEach((cartQuantityInfo) => {
                      const itemsCount = cartQuantityInfo.querySelector("items-count");
                      if (itemsCount) {
                        itemsCount.innerHTML = updatedCount.toString();
                      }
                    });
                  }
                } catch (e) {
                  console.warn("[LIST VIEW DEBUG] Could not update cart state:", e);
                  // If getCart fails, use fetch response count (already set above)
                  console.log("[LIST VIEW DEBUG] Using fetch response count as fallback");
                }
              } else {
                console.warn("[LIST VIEW DEBUG] window.Cart.getCart not available! Using fetch response count.");
              }
              
              // Extract item from cart OR fetch response
              // Since window.Cart might not be available, we need fallback to fetch response
              let itemForNotification = null;
              
              // First, try to get from fetch response (immediate)
              if (data) {
                console.log("[LIST VIEW DEBUG] Checking fetch response data:", data);
                if (data.items && Array.isArray(data.items) && data.items.length > 0) {
                  itemForNotification = data.items[0];
                  console.log("[LIST VIEW DEBUG] Got item from fetch response.items:", itemForNotification);
                } else if (data.product_id || data.variant_id || data.id) {
                  itemForNotification = data;
                  console.log("[LIST VIEW DEBUG] Got item from fetch response data:", itemForNotification);
                }
              }
              
              // Get item from cart (PREFER cart items - have complete data like featured_image, product_title, etc.)
              // Use minimal waits - get from cart if available, otherwise use fetch response immediately
              if (window.Cart && typeof window.Cart.getState === "function") {
                try {
                  // Minimal wait for cart to update after fetch
                  await new Promise(resolve => setTimeout(resolve, 100));
                  
                  const cartState = window.Cart.getState();
                  if (cartState && cartState.cart && cartState.cart.items && cartState.cart.items.length > 0) {
                    // Find the item we just added by variant_id (exact match)
                    const cartItem = cartState.cart.items.find(item => 
                      item.variant_id === parseInt(variantId) || item.id === parseInt(variantId)
                    );
                    
                    // Use exact match if found, otherwise use last item (most recently added)
                    if (cartItem) {
                      itemForNotification = cartItem;
                      console.log("[LIST VIEW DEBUG] Found exact item from cart:", cartItem.product_title);
                    } else {
                      itemForNotification = cartState.cart.items[cartState.cart.items.length - 1];
                      console.log("[LIST VIEW DEBUG] Using last cart item:", itemForNotification.product_title);
                    }
                  }
                } catch (e) {
                  console.warn("[LIST VIEW DEBUG] Could not get cart state, using fetch response:", e);
                }
              }
              
              // Log final itemForNotification status
              if (itemForNotification) {
                console.log("[LIST VIEW DEBUG] Final itemForNotification:", {
                  variant_id: itemForNotification.variant_id,
                  product_title: itemForNotification.product_title,
                  has_featured_image: !!itemForNotification.featured_image,
                  featured_image_url: itemForNotification.featured_image?.url
                });
              } else {
                console.warn("[LIST VIEW DEBUG] itemForNotification is still null after all attempts!");
              }
              
              // Verify item has required properties
              if (itemForNotification) {
                console.log("[LIST VIEW DEBUG] Item for notification:", {
                  variant_id: itemForNotification.variant_id,
                  product_title: itemForNotification.product_title,
                  featured_image: !!itemForNotification.featured_image,
                  vendor: itemForNotification.vendor
                });
              } else {
                console.warn("[LIST VIEW DEBUG] itemForNotification is null!");
              }
              
              // Set latestAddedProduct for notification popup
              if (itemForNotification) {
                console.log("[LIST VIEW DEBUG] Item for notification found:", itemForNotification);
                console.log("[LIST VIEW DEBUG] window.Cart available:", !!window.Cart);
                console.log("[LIST VIEW DEBUG] setState method available:", typeof window.Cart?.setState);
                
                if (window.Cart && typeof window.Cart.setState === "function") {
                  console.log("[LIST VIEW DEBUG] Setting latestAddedProduct with item:", itemForNotification);
                  
                  try {
                    window.Cart.setState("latestAddedProduct", itemForNotification);
                    console.log("[LIST VIEW DEBUG] Called setState");
                    
                    // Verify it was set - check multiple times
                    for (let i = 0; i < 5; i++) {
                      await new Promise(resolve => setTimeout(resolve, 50));
                      const verifyState = window.Cart?.getState?.();
                      console.log("[LIST VIEW DEBUG] Verification attempt", i, "latestAddedProduct:", verifyState?.latestAddedProduct);
                      if (verifyState?.latestAddedProduct) {
                        console.log("[LIST VIEW DEBUG] latestAddedProduct successfully set!");
                        break;
                      }
                    }
                  } catch (e) {
                    console.error("[LIST VIEW DEBUG] Error setting latestAddedProduct:", e);
                  }
                  
                  await new Promise(resolve => setTimeout(resolve, 100));
                } else {
                  console.warn("[LIST VIEW DEBUG] Cannot use setState - not available. Item will be used directly.");
                }
              } else {
                console.error("[LIST VIEW DEBUG] CRITICAL: itemForNotification is null! Cannot proceed.");
              }
              
            if (button) {
              button.classList.remove("loading");
              button.classList.add("added");
              setTimeout(() => {
                button.classList.remove("added");
              }, 1500);
            }
              
              // Check if product-form has notify-on-add attribute
              const productFormElement = form.closest("product-form");
              const shouldNotify = productFormElement && productFormElement.hasAttribute("notify-on-add");
              
              if (shouldNotify) {
                // Reset variants being added (same as product-form.js and grid view)
                if (window.Cart && typeof window.Cart.resetVariantsBeingAdded === "function") {
                  await window.Cart.resetVariantsBeingAdded();
                }
                
                // IMPORTANT: Reset latestAddedProduct BEFORE setting new one to prevent showing old content
                if (window.Cart && typeof window.Cart.resetLatestAddedProduct === "function") {
                  try {
                    await window.Cart.resetLatestAddedProduct();
                    await new Promise(resolve => setTimeout(resolve, 10));
                    console.log("[LIST VIEW DEBUG] Reset latestAddedProduct before setting new");
                  } catch (e) {
                    console.warn("[LIST VIEW DEBUG] Could not reset latestAddedProduct:", e);
                  }
                }
                
                // Get the item to use - prefer cart items (have all properties including featured_image)
                let finalItem = null;
                let cartState = null;
                
                console.log("[LIST VIEW DEBUG] Getting item for notification...");
                console.log("[LIST VIEW DEBUG] itemForNotification:", itemForNotification);
                
                // Try to get from Cart state (cart items have complete data)
                if (window.Cart && typeof window.Cart.getState === "function") {
                  try {
                    cartState = window.Cart.getState();
                    console.log("[LIST VIEW DEBUG] Cart state:", cartState);
                    
                    if (cartState?.cart?.items?.length > 0) {
                      // Find the exact item we just added by variant_id
                      const exactItem = cartState.cart.items.find(item => 
                        item.variant_id === parseInt(variantId) || item.id === parseInt(variantId)
                      );
                      
                      // Use exact match if found, otherwise use last item (most recently added)
                      finalItem = exactItem || cartState.cart.items[cartState.cart.items.length - 1];
                      
                      console.log("[LIST VIEW DEBUG] Selected item from cart:", {
                        variant_id: finalItem?.variant_id,
                        product_title: finalItem?.product_title,
                        has_featured_image: !!finalItem?.featured_image,
                        has_image_url: !!finalItem?.featured_image?.url
                      });
                    } else if (itemForNotification) {
                      // Fallback to itemForNotification from fetch
                      finalItem = itemForNotification;
                      console.log("[LIST VIEW DEBUG] Using itemForNotification from fetch");
                    }
                  } catch (e) {
                    console.error("[LIST VIEW DEBUG] Error getting cart state:", e);
                    if (itemForNotification) {
                      finalItem = itemForNotification;
                    }
                  }
                } else if (itemForNotification) {
                  // Fallback to itemForNotification if Cart not available
                  finalItem = itemForNotification;
                  console.log("[LIST VIEW DEBUG] Using itemForNotification (Cart not available)");
                }
                
                // Set latestAddedProduct with the selected item
                if (finalItem) {
                  // Verify finalItem has required properties
                  if (!finalItem.product_title && finalItem.title) {
                    finalItem.product_title = finalItem.title;
                  }
                  
                  console.log("[LIST VIEW DEBUG] Final item properties:", {
                    product_title: finalItem.product_title || finalItem.title,
                    variant_id: finalItem.variant_id,
                    has_featured_image: !!finalItem.featured_image,
                    has_image_url: !!finalItem.featured_image?.url
                  });
                  
                  if (window.Cart && typeof window.Cart.setState === "function") {
                    try {
                      console.log("[LIST VIEW DEBUG] Setting latestAddedProduct via setState");
                      window.Cart.setState("latestAddedProduct", finalItem);
                      await new Promise(resolve => setTimeout(resolve, 100));
                      
                      // Verify it was set
                      cartState = window.Cart.getState();
                      if (cartState?.latestAddedProduct) {
                        console.log("[LIST VIEW DEBUG] latestAddedProduct set successfully in store");
                      } else {
                        console.warn("[LIST VIEW DEBUG] latestAddedProduct not in state after setState");
                        // Set in local cartState object as fallback
                        if (!cartState) cartState = {};
                        cartState.latestAddedProduct = finalItem;
                      }
                    } catch (e) {
                      console.error("[LIST VIEW DEBUG] Error setting latestAddedProduct:", e);
                      // Set in local cartState object as fallback
                      if (!cartState) cartState = {};
                      cartState.latestAddedProduct = finalItem;
                    }
                  } else {
                    // setState not available - use local cartState
                    cartState = { latestAddedProduct: finalItem };
                    console.log("[LIST VIEW DEBUG] Created local cartState (setState not available)");
                  }
                }
                
                // Final verification
                if (!finalItem) {
                  console.error("[LIST VIEW DEBUG] ERROR: No item available for notification!");
                } else if (!cartState?.latestAddedProduct) {
                  if (!cartState) cartState = {};
                  cartState.latestAddedProduct = finalItem;
                  console.log("[LIST VIEW DEBUG] Set latestAddedProduct in local cartState");
                }
                
                // Find cart-notification element and wait for content to render (same as grid view)
                const cartNotificationElement = document.querySelector("cart-notification");
                console.log("[LIST VIEW DEBUG] CartNotification element:", cartNotificationElement);
                
                // CRITICAL: Force subscription to fire by clearing and resetting latestAddedProduct
                // This ensures CartNotification component updates with new item (like grid view)
                if (cartNotificationElement && cartState?.latestAddedProduct && window.Cart) {
                  const itemToSet = cartState.latestAddedProduct;
                  console.log("[LIST VIEW DEBUG] Forcing subscription to fire for item:", itemToSet.product_title || itemToSet.title);
                  
                  // Step 1: Clear latestAddedProduct to trigger subscription
                  if (typeof window.Cart.resetLatestAddedProduct === "function") {
                    await window.Cart.resetLatestAddedProduct();
                    await new Promise(resolve => setTimeout(resolve, 20));
                    console.log("[LIST VIEW DEBUG] Cleared latestAddedProduct");
                  }
                  
                  // Step 2: Set new item - this should trigger CartNotification subscription
                  if (typeof window.Cart.setState === "function") {
                    window.Cart.setState("latestAddedProduct", itemToSet);
                    await new Promise(resolve => setTimeout(resolve, 50));
                    console.log("[LIST VIEW DEBUG] Set latestAddedProduct:", itemToSet.product_title || itemToSet.title);
                  }
                  
                  // Step 3: Get updated state and verify
                  cartState = window.Cart?.getState?.();
                  console.log("[LIST VIEW DEBUG] After reset/set, latestAddedProduct:", cartState?.latestAddedProduct?.product_title || cartState?.latestAddedProduct?.title);
                  
                  // Step 4: Manually update cartNotificationElement.item to ensure it has latest data
                  if (cartState?.latestAddedProduct) {
                    cartNotificationElement.item = cartState.latestAddedProduct;
                    console.log("[LIST VIEW DEBUG] Updated cartNotificationElement.item");
                  }
                }
                
                // Wait for CartNotification subscription to fire and DOM to update
                if (cartNotificationElement && cartState?.latestAddedProduct) {
                  const currentItem = cartState.latestAddedProduct;
                  console.log("[LIST VIEW DEBUG] Waiting for title to render for:", currentItem.product_title || currentItem.title);
                  
                  // Ensure the element is connected (subscription might not be active if element is hidden)
                  if (!cartNotificationElement.isConnected) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                  }
                  
                  // Set item property directly to ensure it's available
                  cartNotificationElement.item = currentItem;
                  
                  // ALWAYS manually update DOM IMMEDIATELY (like grid view) - no long waits
                  const itemToRender = cartState?.latestAddedProduct || finalItem;
                  if (itemToRender && cartNotificationElement) {
                    console.log("[LIST VIEW DEBUG] IMMEDIATELY updating DOM with item:", itemToRender.product_title || itemToRender.title);
                    
                    // Update cartNotificationElement.item first
                    cartNotificationElement.item = itemToRender;
                    
                    // Update title
                    const titleElement = cartNotificationElement.querySelector("cart-item-title");
                    const itemTitle = itemToRender.product_title || itemToRender.title;
                    if (titleElement && itemTitle) {
                      titleElement.innerHTML = itemTitle;
                      console.log("[LIST VIEW DEBUG] Updated title:", itemTitle);
                    }
                    
                    // Update image if available
                    const imageElement = cartNotificationElement.querySelector("cart-item-image-container img");
                    if (imageElement && itemToRender.featured_image?.url) {
                      const imageData = itemToRender.featured_image;
                      const { url, width, height } = imageData;
                      
                      if (url) {
                        // Calculate srcset (similar to CartNotification component)
                        const srcsetWidths = [120, 240, 360, 480];
                        let srcset = "";
                        const aspectRatio = width && height ? width / height : 1;
                        
                        srcsetWidths.forEach(srcWidth => {
                          if (width && srcWidth <= width) {
                            const srcHeight = Math.round(srcWidth / aspectRatio);
                            srcset += `${url}${url.includes("?") ? "&" : "?"}width=${srcWidth}&height=${srcHeight}&crop=center ${srcWidth}w ${srcHeight}h, `;
                          }
                        });
                        
                        // Set image attributes
                        imageElement.setAttribute("src", url);
                        if (srcset) {
                          imageElement.setAttribute("srcset", srcset.trim().replace(/,\s*$/, ""));
                        }
                        if (width) imageElement.setAttribute("width", width);
                        if (height) imageElement.setAttribute("height", height);
                        imageElement.setAttribute("alt", `Image for ${itemTitle}`);
                        console.log("[LIST VIEW DEBUG] Updated image:", url);
                      }
                    } else if (imageElement && !itemToRender.featured_image) {
                      console.warn("[LIST VIEW DEBUG] Item has no featured_image property!");
                    }
                    
                    // Update variant title if available
                    const variantProperty = cartNotificationElement.querySelector("cart-item-default-property");
                    if (variantProperty) {
                      if (itemToRender.variant_title && !itemToRender.variant_title.includes("Default")) {
                        const metaTemplate = document.getElementById("template-cart-item-meta");
                        if (metaTemplate) {
                          variantProperty.innerHTML = "";
                          variantProperty.appendChild(metaTemplate.content.cloneNode(true));
                          const propValue = variantProperty.querySelector("property-value");
                          if (propValue) {
                            propValue.innerHTML = itemToRender.variant_title;
                            console.log("[LIST VIEW DEBUG] Updated variant title:", itemToRender.variant_title);
                          }
                        }
                      } else {
                        variantProperty.innerHTML = "";
                      }
                    }
                    
                    // Update vendor if available
                    const vendorElement = cartNotificationElement.querySelector("cart-item-vendor");
                    if (vendorElement && itemToRender.vendor) {
                      const metaTemplate = document.getElementById("template-cart-item-meta");
                      if (metaTemplate) {
                        vendorElement.innerHTML = "";
                        vendorElement.appendChild(metaTemplate.content.cloneNode(true));
                        const propValue = vendorElement.querySelector("property-value");
                        if (propValue) {
                          propValue.innerHTML = itemToRender.vendor;
                          console.log("[LIST VIEW DEBUG] Updated vendor:", itemToRender.vendor);
                        }
                      }
                    }
                    
                    // Minimal wait for DOM updates (like grid view)
                    await new Promise(resolve => setTimeout(resolve, 50));
                  } else if (!cartNotificationElement) {
                    // Fallback: wait a bit for CartNotification to initialize
                    await new Promise(resolve => setTimeout(resolve, 100));
                  }
                }
                
                // Open cart notification/drawer (check if not already in cart modal)
                if (!form.closest(".modal--cart")) {
                  // Try popup-dialog first (for popup notification)
                  let cartNotification = document.querySelector(
                    "popup-dialog.popup--cart-notification, popup-dialog#popup-cart-notification"
                  );
                  
                  // Fallback to modal-dialog or elements with classes
                  if (!cartNotification) {
                    cartNotification = document.querySelector(
                      "modal-dialog.modal--cart, .modal--cart, .popup--cart-notification"
                    );
                  }
                  
                  if (cartNotification) {
                    // DISABLE auto-close - remove dismissAfterInterval attribute
                    if (cartNotification.hasAttribute("data-dismiss-after-interval")) {
                      cartNotification.removeAttribute("data-dismiss-after-interval");
                    }
                    
                    // Stop interval if it exists BEFORE opening
                    if (cartNotification.interval) {
                      try {
                        if (typeof cartNotification.interval.stop === "function") {
                          cartNotification.interval.stop();
                        }
                      } catch (e) {}
                      try {
                        if (typeof cartNotification.interval.reset === "function") {
                          cartNotification.interval.reset();
                        }
                      } catch (e) {}
                      cartNotification.interval = null;
                    }
                    
                    // Listen for popup opened event to stop interval if it restarts
                    const stopIntervalOnOpen = () => {
                      if (cartNotification.interval) {
                        try {
                          if (typeof cartNotification.interval.stop === "function") {
                            cartNotification.interval.stop();
                          }
                        } catch (e) {}
                        cartNotification.interval = null;
                      }
                      cartNotification.removeEventListener("on:popup:opened", stopIntervalOnOpen);
                    };
                    cartNotification.addEventListener("on:popup:opened", stopIntervalOnOpen);
                    
                    // Check if it's a popup-dialog or modal-dialog element
                    if (cartNotification.tagName === "POPUP-DIALOG" || cartNotification.tagName === "MODAL-DIALOG") {
                      if (!cartNotification.shown && typeof cartNotification.open === "function") {
                        await cartNotification.open();
                        // Immediately stop interval after opening
                        setTimeout(stopIntervalOnOpen, 50);
                      } else if (typeof cartNotification.open === "function") {
                        await cartNotification.open();
                        setTimeout(stopIntervalOnOpen, 50);
                      }
                    } else {
                      // Legacy support for elements with classes
                      if (!cartNotification.shown && typeof cartNotification.open === "function") {
                        await cartNotification.open();
                        setTimeout(stopIntervalOnOpen, 50);
                      }
                    }
                  } else if (window.theme && window.theme.mfpOpen) {
                    // Fallback to theme's method
                    window.theme.mfpOpen("cart");
                  }
                }
              } else {
                // Fallback: just open cart drawer
                if (window.theme && window.theme.mfpOpen) {
                  window.theme.mfpOpen("cart");
                }
            }
          })
          .catch((error) => {
            if (button) button.classList.remove("loading");
            alert("Fehler beim Hinzufügen zum Warenkorb");
          })
            .finally(() => {
              form.dataset.submitting = "false";
              if (button) {
                button.disabled = false;
                button.classList.remove("is-submitting");
                if (button.dataset.originalClasses) {
                  button.className = button.dataset.originalClasses;
                  delete button.dataset.originalClasses;
                }
              }
            });
        }
      });
    });

    // Re-initialize variant selectors
    const variantSelectors = this.querySelectorAll(".variant-selector");
    variantSelectors.forEach((selector) => {
      // Check if listener already exists to avoid duplicates
      if (selector.dataset.listenerAttached === "true") {
        return;
      }
      selector.dataset.listenerAttached = "true";

      selector.addEventListener("change", () => {
        const variantId = selector.value;
        const productRow = selector.closest("tr");
        const hiddenInput = productRow.querySelector('input[name="id"]');
        if (hiddenInput) {
          hiddenInput.value = variantId;
        }

        // Update price if available
        const productHandle = selector.dataset.handle;
        if (productHandle) {
          fetch(`/products/${productHandle}.js`)
            .then((response) => response.json())
            .then((data) => {
              const variant = data.variants.find((v) => v.id == variantId);
              if (variant) {
                const priceElement = productRow.querySelector(".product-price");
                if (priceElement) {
                  priceElement.innerText = Shopify.formatMoney(
                    variant.price,
                    theme.money_format
                  );
                }
              }
            })
            .catch((error) => console.error("Error updating price:", error));
        }
      });
    });

    // Quantity-input custom elements should auto-connect when added via innerHTML
    // No need to manually re-initialize them as they connect automatically
    // Just ensure they exist in the DOM (which they do after innerHTML update)
  }

  #viewToggleControl() {
    const toggleButtons = this.querySelectorAll(".view-toggle button");
    const collectionTable = this.querySelector(".collection__table-view");
    const collectionCards = this.querySelector(".collection__cards");

    toggleButtons.forEach((button) => {
      const clickHandler = () => {
        toggleButtons.forEach((btn) => btn.classList.remove("active"));
        button.classList.add("active");

        if (button.dataset.view === "list") {
          collectionCards.style.display = "none";
          collectionTable.style.display = "table";
          localStorage.setItem("collection-display-type", "list");
          // Re-initialize table functionality when switching to list view
          this.#initializeTableFunctionality();
        } else {
          collectionCards.style.display = "flex";
          collectionTable.style.display = "none";
          localStorage.setItem("collection-display-type", "grid");
        }
        //makes the grid-products appear
        window.dispatchEvent(new Event("resize"));
      };

      button.addEventListener("click", clickHandler);
      this.listeners.push({
        element: button,
        event: "click",
        handler: clickHandler,
      });
    });

    // Set initial active state based on localStorage
    const savedView = localStorage.getItem("collection-display-type");
    if (savedView === "list") {
      const listButton = this.querySelector('button[data-view="list"]');
      const gridButton = this.querySelector('button[data-view="grid"]');
      if (listButton && gridButton) {
        gridButton.classList.remove("active");
        listButton.classList.add("active");
        if (collectionCards && collectionTable) {
          collectionCards.style.display = "none";
          collectionTable.style.display = "table";
        }
      }
      // Ensure table forms are initialized if list view is active on load
      setTimeout(() => {
        this.#initializeTableFunctionality();
      }, 100);
    } else {
      const listButton = this.querySelector('button[data-view="list"]');
      const gridButton = this.querySelector('button[data-view="grid"]');
      if (listButton && gridButton) {
        listButton.classList.remove("active");
        gridButton.classList.add("active");
        if (collectionCards && collectionTable) {
          collectionCards.style.display = "flex";
          collectionTable.style.display = "none";
        }
      }
      // Initialize table forms even when hidden (for when user switches to list view)
      setTimeout(() => {
        this.#initializeTableFunctionality();
      }, 100);
    }
  }
}

customElements.define("dynamic-product-search", DynamicProductSearch);
//# sourceMappingURL=dynamic-product-search.js.map
